import { forwardRef, useEffect, useRef, useState } from "react";
import TextareaAutosize from "react-autosize-textarea";
import DayPickerInput from "react-day-picker/DayPickerInput";
import { BsChevronDown } from "react-icons/bs";
import { uuidv4 } from "../../utils/uuid.js";
import { Tag } from "./base.js";
import { InlineSpinner } from "./Spinner";

export { default as SearchableSelect } from "./SearchableSelect";

const height = 30;

export const Input = forwardRef(
  (
    {
      className = "",
      border = "border border-gray-200 dark:border-gray-800",
      bgColor = "dark:bg-gray-800",
      validated = true,
      number = false,
      required = false,
      label,
      value,
      disabled = false,
      readOnly = false,
      ...rest
    },
    ref,
  ) => {
    if (readOnly) return value || "";

    const isNumberValid = (() => {
      if (!number) return true;
      if (value === null || value === undefined) return false;
      if (typeof value === "number") return Number.isFinite(value);

      const text = String(value);
      if (text === "" || /\s/.test(text)) return false;

      const parsed = Number(text);
      return Number.isFinite(parsed);
    })();
    const finalValidated = validated && isNumberValid;

    return (
      <input
        className={`px-2 rounded-lg appearance-none placeholder-gray-300 dark:placeholder-gray-700 dark:text-gray-200 disabled:cursor-not-allowed max-w-full
              ${border}
              ${finalValidated ? "input-focus-primary-gradient" : "border-red-500 dark:border-red-800 bg-red-50 dark:bg-red-900"}
              ${bgColor}
              ${className}
              `}
        style={{ height }}
        value={value === null || value === undefined ? "" : value}
        disabled={disabled}
        ref={ref}
        {...rest}
      />
    );
  },
);

export const Switcher = ({
  className = "",
  isOn,
  onChange,
  disabled,
  loading,
}) => (
  <div
    className={`
          flex items-center rounded-full bg-white duration-200 relative
          ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer "}
          ${isOn ? "bg-green-500" : "bg-gray-300 dark:bg-gray-700"}
          ${className}
          `}
    style={{ width: 42, height: 24 }}
    onClick={disabled ? null : onChange}
  >
    <div
      style={{ width: 20, height: 20, left: isOn ? 20 : 2 }}
      className="rounded-full duration-200 absolute bg-white dark:bg-gray-100"
    ></div>
    {loading ? (
      <div className="relative pl-14">
        <InlineSpinner size={18} text={null} />
      </div>
    ) : null}
  </div>
);

export const TapSelector = ({ className = "", options, value, onChange }) => {
  const [selected, setSelected] = useState(value);
  return (
    <div
      className={`flex items-center rounded-full bg-white cursor-pointer relative ${className}`}
    >
      <div className="flex">
        {options.map((i, index) => (
          <Tag
            key={index}
            selected={selected === i}
            className={`animate-ease-2 mr-4`}
            title={i}
            onClick={() => {
              if (!onChange) return;
              setSelected(i);
              onChange(i);
            }}
          />
        ))}
      </div>
    </div>
  );
};

export const MultipleTapSelector = ({
  className = "",
  options,
  value,
  onChange,
}) => {
  const [selected, setSelected] = useState(value);
  return (
    <div
      className={`flex items-center rounded-full bg-white relative ${className}`}
    >
      <div className="flex flex-wrap">
        {options.map((i, index) => (
          <Tag
            key={index}
            selected={selected.indexOf(i) > -1}
            className={`animate-ease-2 mr-4 mb-2`}
            title={i}
            onClick={() => {
              if (!onChange) return;
              let res = [...selected];
              if (selected.indexOf(i) > -1) {
                res = selected.filter((prevItem) => prevItem !== i);
              } else {
                res = [...selected, i];
              }
              setSelected(res);
              onChange(res);
            }}
          />
        ))}
      </div>
    </div>
  );
};

export const Select = ({
  className = "",
  textAlignLast = "",
  height = 30,
  plain = false,
  loading = false,
  error,
  children,
  ...rest
}) => (
  <select
    className={`${className} cursor-pointer max-w-full overflow-auto
         ${
           plain
             ? ""
             : "border border-gray-200 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-200"
         } rounded-lg px-2 ${plain ? "" : "input-focus-primary-gradient"} disabled:bg-gray-100 disabled:cursor-not-allowed max-w-full`}
    style={{ height, textAlignLast }}
    {...rest}
  >
    {loading ? (
      <option disabled>Loading...</option>
    ) : error ? (
      <option disabled>{error}</option>
    ) : (
      children
    )}
  </select>
);

export const LongSelect = ({
  value,
  onChange,
  options,
  placeholder,
  disabled,
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = options.find((opt) => opt.value === value);

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-2 rounded-lg border border-gray-200 bg-white dark:bg-gray-800 dark:border-gray-700 dark:text-gray-200 focus:border-sky-600 disabled:opacity-80 disabled:cursor-not-allowed min-w-0"
        style={{ height: 30 }}
      >
        <span className="truncate flex-1 text-left">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <BsChevronDown
          className={`flex-shrink-0 ml-2 transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 max-h-60 overflow-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
          {options.map((option) => (
            <div
              key={option.value}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              className={`px-3 py-2 cursor-pointer text-sm break-words transition-colors duration-150 border-b border-gray-100 dark:border-gray-700 last:border-b-0 ${
                option.value === value
                  ? "bg-sky-100 dark:bg-sky-900 text-sky-700 dark:text-sky-200"
                  : "hover:bg-gray-100 dark:hover:bg-gray-700 active:bg-gray-200 dark:active:bg-gray-600"
              }`}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const Text = ({
  className = "",
  value,
  readOnly = false,
  bgColor = "dark:bg-gray-800",
  ...rest
}) => {
  if (readOnly) return value || "";

  return (
    <TextareaAutosize
      rows={3}
      className={`${className} ${bgColor} w-full px-3 py-2 text-sm rounded-xl appearance-none border border-gray-200 input-focus-primary-gradient dark:border-gray-700 dark:text-gray-200 placeholder:dark:opacity-50 disabled:cursor-not-allowed`}
      value={value || ""}
      {...rest}
    />
  );
};

const CustomDateInput = (props, ref) => (
  <Input className="text-center" {...props} />
);

export const DatePicker = ({
  className = "",
  dayPickerProps = {},
  ...rest
}) => (
  <div className="relative rounded-lg overflow-visible">
    <DayPickerInput
      classNames={{
        container: `${className} relative`,
        overlay:
          "leading-none shadow-lg rounded-lg absolute bg-white dark:bg-gray-900 z-30",
      }}
      component={forwardRef(CustomDateInput)}
      dayPickerProps={dayPickerProps}
      {...rest}
    />
    {rest.disabled ? (
      <div className="absolute inset-0 cursor-not-allowed bg-gray-400 bg-opacity-10"></div>
    ) : null}
  </div>
);

export const NativeDatePicker = ({ className = "", value, ...rest }) => (
  <input
    type="date"
    className={`text-center border rounded-lg px-2 h-[30px] input-focus-primary-gradient dark:bg-gray-800 dark:border-gray-800 disabled:cursor-not-allowed ${className}`}
    value={value || ""}
    {...rest}
  />
);

export const DateInput = ({ className = "", ...rest }) => (
  <Input type="date" className={` ${className}`} {...rest} />
);

export const FileSelector = ({
  className = "",
  title = "add files",
  bold = false,
  bgColor = "",
  onChange,
  uploading,
  ...rest
}) => {
  const [mouseIn, setMouseIn] = useState(false);
  const [uid] = useState(uuidv4());
  const borderStyle = "text-sky-600";
  const hoverBorderStyle = "text-sky-700";
  return (
    <div
      className={`${className} overflow-hidden relative`}
      onMouseEnter={() => setMouseIn(true)}
      onMouseLeave={() => setMouseIn(false)}
    >
      <label
        className={`relative font-semibold opacity-100
               ${rest.disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}
               ${borderStyle}
               ${mouseIn ? hoverBorderStyle : ""}
               ${bgColor}
               `}
        htmlFor={uid}
      >
        {uploading ? "uploading..." : title}
      </label>

      <input
        id={uid}
        className="hidden"
        type="file"
        multiple
        required
        onChange={onChange}
        disabled={uploading || rest.disabled}
        {...rest}
      />
    </div>
  );
};

export const Field = ({
  id,
  label,
  value,
  labelWidth = "w-auto",
  onChange,
  suffix,
  tips,
  placeholder = "",
  ...rest
}) => (
  <div className="flex space-x-4 items-baseline">
    <label htmlFor={id} className={`capitalize ${labelWidth}`}>
      {label}:
    </label>

    <div className="flex-1">
      <div className="flex space-x-4 items-center">
        <Input
          id={id}
          placeholder={placeholder}
          className={suffix ? "" : "flex-1"}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          {...rest}
        />
        {suffix ? <div>{suffix}</div> : null}
      </div>
      {tips ? (
        <div className="mt-1 ml-2 text-xs text-gray-500">{tips}</div>
      ) : null}
    </div>
  </div>
);
